import type { FC } from 'react';

import { ProductCategoriesTrackingAction, ProductCategoriesTrackingCategory } from '@zen/Products/types';
import { useNotification } from '@zen/utils/hooks/useNotification';
import useTracking from '@zen/utils/hooks/useTracking';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import ProductCategoryForm from '../components/ProductCategoryForm';
import { useCreateProductCategoryMutation } from '../graphql';
import type { CoreProductCategory } from '../types';

interface Props {
  accountId: string;
  onClose: () => void;
  onSuccess?: (categoryId: string) => void;
  source?: 'ProductForm' | 'ProductSettings';
}

const CreateProductCategory: FC<Props> = ({ accountId, onClose, onSuccess, source = 'ProductSettings' }) => {
  const { addSuccess, addError } = useNotification();
  const { trackEvent } = useTracking();
  const [createCategory] = useCreateProductCategoryMutation();

  const trackCategoryCreation = (categoryName: string): void => {
    trackEvent({
      category: ProductCategoriesTrackingCategory,
      action: ProductCategoriesTrackingAction.CATEGORY_CREATED,
      label: categoryName,
      properties: {
        categoryName,
        source
      }
    });
  };

  const handleSubmit = async (values: Pick<CoreProductCategory, 'name'>): Promise<IOkOrErrorResult> => {
    trackCategoryCreation(values.name);

    return performFormMutation({
      mutationFn: () =>
        createCategory({
          awaitRefetchQueries: true,
          refetchQueries: ['categories', 'productCategories'],
          variables: {
            input: {
              accountId,
              name: values.name
            }
          }
        }),
      onError: () => addError()
    });
  };

  const handleSuccess = (categoryId: string): void => {
    addSuccess('Product category added successfully.');
    onSuccess?.(categoryId);
    onClose();
  };

  return (
    <ProductCategoryForm
      buttonText="Create category"
      initialValues={{ name: '' }}
      onCancel={onClose}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
    />
  );
};

export default CreateProductCategory;
